/* eslint-disable react-hooks/exhaustive-deps */
/*
 * 🐉 DragonHeir Dragon Class Store — /dragon-class-store (Feb 2026)
 * -------------------------------------------------------------------
 * Owner-scoped TEASE build. Dragons aren't live yet, so this page is
 * strictly read-only:
 *   • Per-class rails (Mage first; others "Coming soon")
 *   • Greyed/locked Wyrmcall card with the owner-supplied dragon icon
 *   • Book balance in the Mage rail header (💜 X Mage Books)
 * No buy, no install, no overlay, no reserve, no swap. Those ship with
 * the dragon release and plug into dragon_books / dragons.<class>.owned.
 */

import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Lock, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DragonClassStorePage() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/dragon-class-store`, { withCredentials: true });
        if (alive) setState(data);
      } catch (_e) {
        if (alive) setState({ classes: [], balances: {}, purchases_enabled: false });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [user?.user_id]);

  if (loading) {
    return (
      <div className="min-h-[80vh] bg-black text-white flex items-center justify-center">
        <div className="text-neutral-500 uppercase tracking-widest text-xs">Opening the Dragon Class Store…</div>
      </div>
    );
  }

  const mageRail = (state?.classes || []).find((c) => c.class_id === "mage") || null;
  const archerRail = (state?.classes || []).find((c) => c.class_id === "archer") || null;
  const warriorRail = (state?.classes || []).find((c) => c.class_id === "warrior" && !c.coming_soon) || null;
  const ashRogueRail = (state?.classes || []).find((c) => c.class_id === "ash_rogue" && !c.coming_soon) || null;
  // 🖤 Dark — Requiem, the Dark One (reframed from Glitch / Nullheir).
  // 🤍 Holy — Cilvilnous, the Holy One (class not yet ruled; colorless).
  const darkRail = (state?.classes || []).find((c) => c.class_id === "dark" && !c.coming_soon) || null;
  const holyRail = (state?.classes || []).find((c) => c.class_id === "holy" && !c.coming_soon) || null;
  const futureRails = (state?.classes || []).filter((c) => c.coming_soon);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0A0A] via-[#120820] to-[#0A0A0A] text-white"
         data-testid="dragon-class-store-page">
      {/* ─── Header ─────────────────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-5 pt-6 pb-4">
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-neutral-500 hover:text-[#A78BFA] transition-colors"
          data-testid="back-to-shop"
        >
          <ArrowLeft size={14} /> Back to Shop
        </Link>
        <div className="mt-4 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#A78BFA] font-bold">
              🐉 DragonHeir
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-white mt-1 leading-none">
              Dragon Class Store
            </h1>
            <p className="text-sm text-neutral-400 mt-2 max-w-xl leading-snug">
              Skills for your dragons, paid in class-colored Dragon Books.
              Dragons are coming soon — the Mage sanctum is open, one skill
              inked and waiting.
            </p>
          </div>
        </div>
      </div>

      {/* ─── Mage Rail ───────────────────────────────────────────── */}
      {mageRail && (
        <div className="max-w-6xl mx-auto px-5 pb-10" data-testid="dragon-rail-mage">
          <div className="border border-[#A78BFA]/40 bg-gradient-to-br from-[#1a0c2e] via-[#120820] to-[#0A0A0A]">
            {/* Rail header */}
            <div className="px-5 py-4 border-b border-[#A78BFA]/20 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                {/* Dragon avatar (greyed when not owned) */}
                <div className="relative w-14 h-14 rounded-full overflow-hidden border border-[#A78BFA]/60 bg-black">
                  <img
                    src={mageRail.dragon_icon}
                    alt="Mage Dragon"
                    className={`w-full h-full object-cover ${mageRail.owned ? "" : "grayscale opacity-40"}`}
                    draggable={false}
                  />
                  {!mageRail.owned && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <Lock size={16} className="text-neutral-300" />
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#A78BFA] font-bold">
                    💜 Mage Class
                  </div>
                  <div className="font-display text-xl text-white leading-tight">
                    {mageRail.owned ? "Your Mage Dragon" : "Mage Dragon · Coming Soon"}
                  </div>
                </div>
              </div>
              {/* Book balance */}
              <div
                className="flex items-center gap-2 bg-[#A78BFA]/10 border border-[#A78BFA]/40 px-3 py-2"
                data-testid="mage-book-balance"
              >
                <span className="text-xl leading-none">💜</span>
                <div className="leading-tight">
                  <div className="font-mono tabular-nums text-lg text-[#C4B5FD]">
                    {(mageRail.book_balance || 0).toLocaleString()}
                  </div>
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500">
                    Mage Books
                  </div>
                </div>
              </div>
            </div>

            {/* Skill grid */}
            <div className="px-5 py-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-3">
                Mage Skills
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {(mageRail.skills || []).map((s) => (
                  <MageSkillCard
                    key={s.id}
                    skill={s}
                    owned={mageRail.owned}
                  />
                ))}
              </div>
            </div>

            {/* Footer note */}
            <div className="px-5 pb-5 pt-2 text-[11px] text-neutral-500 italic leading-snug">
              💜 1 Mage Book = 1 skill. Books earn their way into the game
              slowly — keep an eye out. Install, overlay, reserve &amp; the
              Forget-me-not Flower all unlock when dragons land.
            </div>
          </div>
        </div>
      )}

      {/* ─── Archer Rail (Stormwing Archer · Role: Fast) ─────────── */}
      {archerRail && <ClassRail rail={archerRail} />}

      {/* ─── Warrior Rail (Drakeforged Warrior · Role: Aggressor) ── */}
      {warriorRail && <ClassRail rail={warriorRail} />}

      {/* ─── Ash Rogue Rail (Emberveil Rogue · Role: Defensive / Redirection) ─ */}
      {ashRogueRail && <ClassRail rail={ashRogueRail} />}

      {/* ─── Dark Rail (Requiem, the Dark One · Role: Dark attacks) ──────── */}
      {darkRail && <ClassRail rail={darkRail} />}

      {/* ─── Holy Rail (Cilvilnous, the Holy One · Role: Holy attacks) ────
          Class not yet ruled; colorless sixth dragon (empty hex → neutral). */}
      {holyRail && <ClassRail rail={holyRail} />}

      {/* ─── Future class rails (Coming Soon stubs) ─────────────── */}
      {futureRails.length > 0 && (
        <div className="max-w-6xl mx-auto px-5 pb-12">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-3">
            Other Classes
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {futureRails.map((c) => (
              <div
                key={c.class_id}
                data-testid={`future-rail-${c.class_id}`}
                className="border border-zinc-800 bg-[#0A0A0A] p-4 flex flex-col items-center text-center"
              >
                <Lock size={16} className="text-neutral-600 mb-2" />
                <div className="font-display text-sm text-neutral-300">{c.label}</div>
                <div className="text-[9px] uppercase tracking-widest text-neutral-600 mt-1">
                  Coming soon
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Wyrmcall / Mage Skill Card (locked tease) ───────────────── */
function MageSkillCard({ skill, owned }) {
  const locked = !owned;
  return (
    <div
      data-testid={`mage-skill-${skill.id}`}
      className={`relative border p-4 flex flex-col gap-2 transition-colors ${
        locked
          ? "border-zinc-800 bg-[#0A0A0A]"
          : "border-[#A78BFA]/60 bg-[#120820] hover:border-[#C4B5FD]"
      }`}
    >
      {/* Icon tile — greyed when locked */}
      <div className="relative w-full aspect-square overflow-hidden bg-black border border-zinc-900">
        <img
          src={skill.icon_url}
          alt={skill.name}
          className={`w-full h-full object-cover ${locked ? "grayscale opacity-40" : ""}`}
          draggable={false}
        />
        {locked && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 gap-1">
            <Lock size={22} className="text-neutral-300" />
            <div className="text-[9px] uppercase tracking-widest text-neutral-300 text-center px-2 leading-tight">
              🔒 Mage Dragon<br/>required
            </div>
          </div>
        )}
      </div>

      {/* Name + kind badge */}
      <div className="flex items-start justify-between gap-2 mt-1">
        <div className={`font-display text-lg leading-tight ${locked ? "text-neutral-400" : "text-white"}`}>
          {skill.name}
        </div>
        <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${
          skill.kind === "active"
            ? "text-[#C4B5FD] border-[#A78BFA]/40 bg-[#A78BFA]/10"
            : "text-neutral-400 border-zinc-700 bg-zinc-900"
        }`}>
          {skill.kind}
        </span>
      </div>

      {/* Effect text */}
      <div className={`text-[11px] leading-snug min-h-[2.5em] ${locked ? "text-neutral-500" : "text-neutral-300"}`}>
        {skill.effect}
      </div>

      {/* Stat row — DPP cost & cooldown */}
      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-neutral-500 border-t border-zinc-900 pt-2 mt-1">
        <div>
          <span className="text-neutral-400">{skill.dpp_cost}</span>
          <span className="ml-1">DPP · used</span>
        </div>
        <div>
          <span className="text-neutral-400">{skill.cooldown}</span>
          <span className="ml-1">turn CD</span>
        </div>
      </div>

      {/* Price + lock button */}
      <div className="flex items-center justify-between mt-2">
        <div className="font-mono tabular-nums text-base text-[#C4B5FD]">
          💜 {skill.book_price} <span className="text-xs text-neutral-500 uppercase tracking-widest">Book</span>
        </div>
        <button
          disabled
          data-testid={`mage-skill-buy-${skill.id}`}
          className="bg-zinc-900 text-neutral-500 text-[10px] uppercase tracking-widest px-3 py-1.5 cursor-not-allowed border border-zinc-800"
          title="Dragons coming soon"
        >
          🔒 Soon
        </button>
      </div>
    </div>
  );
}


/* ─── Generic Class Rail (used by Archer / future classes) ──────
 * Mirrors the Mage rail markup but is data-driven on `rail.hex` so
 * each class can tint itself with its canon color.
 */
function ClassRail({ rail }) {
  if (!rail) return null;
  // Fallback is neutral grey, not mage purple — so the colorless Holy rail
  // (empty hex per owner canon) renders neutral instead of purple.
  const hex = rail.hex || "#9CA3AF";
  // Readability override: Requiem's canon color is pure black (#000000), so
  // using it directly for TEXT against the dark page gradient makes the rail
  // unreadable. Keep `hex` as the semantic/border color but swap to white
  // for text/number display when the color is near-black.
  const isBlack = /^#0{3,6}$/i.test(hex) || hex.toLowerCase() === "#000000";
  const textHex = isBlack ? "#F5F5F5" : hex;
  // emoji may be an intentionally-empty string (colorless Holy rail per
  // owner canon). Only fall back to the generic dragon glyph when the
  // field is actually missing (null/undefined) — never override an empty
  // string that the backend chose on purpose.
  const emoji = rail.emoji === undefined || rail.emoji === null ? "🐉" : rail.emoji;
  const label = rail.label || "Dragon";
  const dragonName = rail.dragon_name || `${label} Dragon`;
  const role = rail.role || null;
  const owned = !!rail.owned;
  const iconUrl = rail.dragon_icon || "";

  return (
    <div
      className="max-w-6xl mx-auto px-5 pb-10"
      data-testid={`dragon-rail-${rail.class_id}`}
    >
      <div
        className="border bg-gradient-to-br from-[#061a26] via-[#071320] to-[#0A0A0A]"
        style={{ borderColor: `${hex}66` }}
      >
        {/* Rail header */}
        <div
          className="px-5 py-4 flex items-center justify-between flex-wrap gap-3 border-b"
          style={{ borderColor: `${hex}33` }}
        >
          <div className="flex items-center gap-3">
            {/* Dragon avatar (locked when no owner image / not owned) */}
            <div
              className="relative w-14 h-14 rounded-full overflow-hidden border bg-black"
              style={{ borderColor: `${hex}99` }}
            >
              {iconUrl ? (
                <img
                  src={iconUrl}
                  alt={dragonName}
                  className={`w-full h-full object-cover ${owned ? "" : "grayscale opacity-40"}`}
                  draggable={false}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-2xl bg-black">
                  {emoji}
                </div>
              )}
              {(!owned || !iconUrl) && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                  <Lock size={16} className="text-neutral-300" />
                </div>
              )}
            </div>
            <div>
              <div
                className="text-[10px] uppercase tracking-[0.3em] font-bold"
                style={{ color: textHex }}
              >
                {emoji} {label} Class
              </div>
              <div className="font-display text-xl text-white leading-tight">
                {owned ? `Your ${dragonName}` : `${dragonName} · Coming Soon`}
              </div>
              {role && (
                <div
                  className="inline-block mt-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 border"
                  style={{
                    color: textHex,
                    borderColor: `${hex}66`,
                    backgroundColor: `${hex}1A`,
                  }}
                  data-testid={`dragon-role-${rail.class_id}`}
                >
                  Role · {role}
                </div>
              )}
            </div>
          </div>
          {/* Book balance */}
          <div
            className="flex items-center gap-2 px-3 py-2 border"
            style={{
              backgroundColor: `${hex}1A`,
              borderColor: `${hex}66`,
            }}
            data-testid={`${rail.class_id}-book-balance`}
          >
            <span className="text-xl leading-none">{emoji}</span>
            <div className="leading-tight">
              <div
                className="font-mono tabular-nums text-lg"
                style={{ color: textHex }}
              >
                {(rail.book_balance || 0).toLocaleString()}
              </div>
              <div className="text-[9px] uppercase tracking-widest text-neutral-500">
                {label} Books
              </div>
            </div>
          </div>
        </div>

        {/* Skill grid */}
        <div className="px-5 py-5">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-3">
            {label} Skills
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(rail.skills || []).map((s) => (
              <ClassSkillCard
                key={s.id}
                skill={s}
                owned={owned}
                classId={rail.class_id}
                hex={hex}
                emoji={emoji}
                label={label}
              />
            ))}
          </div>
        </div>

        {/* Footer note */}
        <div className="px-5 pb-5 pt-2 text-[11px] text-neutral-500 italic leading-snug">
          {emoji} 1 {label} Book = 1 skill. Books earn their way into the game
          slowly — keep an eye out. Install, overlay, reserve &amp; the
          Forget-me-not Flower all unlock when dragons land.
        </div>
      </div>
    </div>
  );
}

/* ─── Generic Skill Card (used by Archer / future classes) ────── */
function ClassSkillCard({ skill, owned, classId, hex, emoji, label }) {
  const locked = !owned;
  const cdUnit = skill.cooldown_unit === "second" ? "s CD" : " turn CD";
  const roleTag = skill.role_tag ? skill.role_tag : null;
  return (
    <div
      data-testid={`${classId}-skill-${skill.id}`}
      className="relative border p-4 flex flex-col gap-2 transition-colors"
      style={{
        borderColor: locked ? "#27272a" : `${hex}99`,
        backgroundColor: locked ? "#0A0A0A" : "#07131f",
      }}
    >
      {/* Icon tile — placeholder when no image, greyed when locked */}
      <div className="relative w-full aspect-square overflow-hidden bg-black border border-zinc-900">
        {skill.icon_url ? (
          <img
            src={skill.icon_url}
            alt={skill.name}
            className={`w-full h-full object-cover ${locked ? "grayscale opacity-40" : ""}`}
            draggable={false}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-5xl bg-black">
            {emoji}
          </div>
        )}
        {(locked || !skill.icon_url) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 gap-1">
            <Lock size={22} className="text-neutral-300" />
            <div className="text-[9px] uppercase tracking-widest text-neutral-300 text-center px-2 leading-tight">
              🔒 {label} Dragon<br/>required
            </div>
          </div>
        )}
      </div>

      {/* Name + kind badge */}
      <div className="flex items-start justify-between gap-2 mt-1">
        <div className={`font-display text-lg leading-tight ${locked ? "text-neutral-400" : "text-white"}`}>
          {skill.name}
        </div>
        <span
          className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border"
          style={
            skill.kind === "active"
              ? { color: hex, borderColor: `${hex}66`, backgroundColor: `${hex}1A` }
              : { color: "#a1a1aa", borderColor: "#3f3f46", backgroundColor: "#18181b" }
          }
        >
          {skill.kind}
        </span>
      </div>

      {/* Optional role tag (e.g. "generator") */}
      {roleTag && (
        <div
          className="self-start text-[9px] uppercase tracking-widest px-1.5 py-0.5 border"
          style={{ color: hex, borderColor: `${hex}66`, backgroundColor: "transparent" }}
          data-testid={`${classId}-skill-${skill.id}-role-tag`}
        >
          {roleTag}
        </div>
      )}

      {/* Effect text */}
      <div className={`text-[11px] leading-snug min-h-[2.5em] ${locked ? "text-neutral-500" : "text-neutral-300"}`}>
        {skill.effect}
      </div>

      {/* Stat row — DPP cost & cooldown */}
      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-neutral-500 border-t border-zinc-900 pt-2 mt-1">
        <div>
          <span className="text-neutral-400">{skill.dpp_cost}</span>
          <span className="ml-1">DPP · used</span>
        </div>
        <div data-testid={`${classId}-skill-${skill.id}-cooldown`}>
          <span className="text-neutral-400">{skill.cooldown}</span>
          <span className="ml-1">{cdUnit}</span>
        </div>
      </div>

      {/* Price + lock button */}
      <div className="flex items-center justify-between mt-2">
        <div className="font-mono tabular-nums text-base" style={{ color: hex }}>
          {emoji} {skill.book_price}{" "}
          <span className="text-xs text-neutral-500 uppercase tracking-widest">Book</span>
        </div>
        <button
          disabled
          data-testid={`${classId}-skill-buy-${skill.id}`}
          className="bg-zinc-900 text-neutral-500 text-[10px] uppercase tracking-widest px-3 py-1.5 cursor-not-allowed border border-zinc-800"
          title="Dragons coming soon"
        >
          🔒 Soon
        </button>
      </div>
    </div>
  );
}
